/**
 * Cookie consent — the state behind the banner.
 *
 * One stored value, `consent_v1`: {choice: 'granted'|'denied', ts}. Nothing else
 * about the visitor is kept. The bootstrap in index.cs.html reads the SAME key
 * with the same expiry, so a returning visitor's first pageview is already
 * measured (or not) before React has loaded — this module must not drift from it.
 *
 * Expiry is twelve months. An expired choice is not a denial: it means we have to
 * ask again, which is why `readConsent` returns null for both "never asked" and
 * "asked too long ago" — the banner treats them identically.
 */

export const CONSENT_KEY = "consent_v1";
export const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

/** The stored choice, or null when we have to ask (never asked / expired / junk). */
export function readConsent(now = Date.now(), store = safeStore()) {
  try {
    const raw = store && store.getItem(CONSENT_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c?.choice !== "granted" && c?.choice !== "denied") return null;
    if (typeof c.ts !== "number" || now - c.ts >= CONSENT_MAX_AGE_MS) return null;
    return c.choice;
  } catch (e) {
    return null;
  }
}

/** Record a choice and tell Google about it. Returns the choice, for chaining. */
export function writeConsent(choice, now = Date.now(), store = safeStore()) {
  try {
    store && store.setItem(CONSENT_KEY, JSON.stringify({ choice, ts: now }));
  } catch (e) {
    // A blocked localStorage must not swallow the click: the update below still
    // applies for this page view, we just have to ask again next time.
  }
  updateGtagConsent(choice);
  return choice;
}

/** Push the choice into Consent Mode. No-op when gtag never loaded (other builds). */
export function updateGtagConsent(choice, w = typeof window !== "undefined" ? window : null) {
  if (!w || typeof w.gtag !== "function") return false;
  w.gtag("consent", "update", {
    analytics_storage: choice === "granted" ? "granted" : "denied",
  });
  return true;
}

function safeStore() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (e) {
    return null; // Safari in private mode throws on access, not on use
  }
}
